import { useEffect, useRef, useState } from 'react';
import { Divider } from 'antd';
import { useLocation } from 'react-router';
import { TrackerCustomEvent } from 'common-package';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { List } from 'antd-mobile';
import { Header, ScrollView, SlideSelect } from '@/components';
import { selectedStoreInfo } from '@/store/device';
import { useNav, useTracker } from '@/hooks';
import { DAYJS_FORMAT } from '@/constant';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import service from '@/service';
import ServiceRecordDrawer, { ServiceRecordDrawerRefAttrs } from './components/ServiceRecordDrawer';
import WeekTimeSelect from './components/WeekTimeSelect';
import ServiceTimesCard from './components/ServiceTimesCard';
import ServiceRecordCard from './components/ServiceRecordCard';
import StatisticsInfo from './components/StatisticsInfo';
import EmptyBox from './components/EmptyBox';
import MemberRecord from './components/MemberRecord';
import cx from './index.module.scss';

type TabsKey = 'member-record' | 'service-record';

export type ZoomParams = {
  zoomStartValue?: string;
  zoomEndValue?: string;
};

const Title = () => {
  const storeInfo = useSelector(selectedStoreInfo);
  return (
    <div className={cx('title')}>
      <span className={cx('shop-name')}>{storeInfo?.name}</span>
      的服务详情
    </div>
  );
};

const ServiceRecord = () => {
  const [currentTabKey, setCurrentTabKey] = useState<TabsKey>('service-record');
  const [currentDate, setCurrentDate] = useState(dayjs().format(DAYJS_FORMAT.DATE));
  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const nav = useNav();
  const location = useLocation();
  const contentRef = useRef(null);
  const [serviceRecords, setServiceRecords] = useState<ServiceRecordItem[]>(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const drawerRef = useRef<ServiceRecordDrawerRefAttrs>(null);
  const recordPointInfoRef = useRef({
    clickGoToSupplyBtnTimes: 0,
    clickRecordCardTimes: 0,
  });
  const { sendEvent } = useTracker();
  const isFirstLoad = useRef<boolean>(true);

  const [storeOrderInfo, setStoreOrderInfo] = useState<StoreOrderInfo>({
    totalOrders: 0,
    totalCustomers: 0,
  });

  const addClickRecordCardTimes = () => {
    recordPointInfoRef.current.clickRecordCardTimes += 1;
  };

  const addClickGoToSupplyBtnTimes = () => {
    recordPointInfoRef.current.clickGoToSupplyBtnTimes += 1;
  };

  const getTotalInfo = async () => {
    const { data } = await service.serviceRecordApi.getStoreOrderInfo();
    setStoreOrderInfo(data);
  };

  const getServiceRecords = async currentDate => {
    try {
      const { data } = await service.serviceRecordApi.getServiceRecords(currentDate);
      setServiceRecords(data);
    } finally {
      scrollViewRef.current?.handleRefresh();
    }
  };

  const handleDateChange = async currentDate => {
    setCurrentDate(currentDate);
    isFirstLoad.current = false;
    getServiceRecords(currentDate);
  };

  const onRefresh = () => {
    // 初次加载不执行
    if (!isFirstLoad.current) {
      scrollViewRef.current.bsInstance.scrollToElement(contentRef.current, 100, 0, 0);
    }
  };

  useEffect(() => {
    if (sendEvent) {
      return () => {
        sendEvent(TrackerCustomEvent.LeaveServiceRecordPage, {
          et: 'CLK',
          c1: recordPointInfoRef.current.clickRecordCardTimes,
          c2: recordPointInfoRef.current.clickGoToSupplyBtnTimes,
        });
      };
    }
  }, [sendEvent]);

  useEffect(() => {
    // 初始获取累计数值
    getTotalInfo();
    getServiceRecords(currentDate);
    // 初始加载时候
    if (location.hash) {
      setCurrentTabKey('member-record');
    }
    // 获取初始页面的高度 包括键盘的高度+可视区域
    const scrollHeight = document.documentElement.scrollHeight;
    setScrollHeight(scrollHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('user-service-detail')} style={{ height: scrollHeight + 'px' }}>
      <Header title={<Title />} showBack={false} showBorder={false} />
      <Divider style={{ marginTop: 0 }} />
      <div className={cx('container')}>
        <SlideSelect
          steps={[
            { value: 'service-record', label: '服务记录' },
            { value: 'member-record', label: '会员记录' },
          ]}
          value={currentTabKey}
          className={cx('slide-select')}
          onChange={value => {
            setCurrentTabKey(value as TabsKey);
            if (value === 'member-record') {
              // 页面埋点 添加页面Hash值
              nav(location.pathname + `#${value}`);
              sendEvent(TrackerCustomEvent.ViewMemberRecordClk, { et: 'CLK' });
            } else {
              getTotalInfo();
              setCurrentDate(dayjs().format(DAYJS_FORMAT.DATE));
              getServiceRecords(dayjs().format(DAYJS_FORMAT.DATE));
              isFirstLoad.current = true;
              nav(location.pathname);
            }
          }}
        />
        {currentTabKey === 'service-record' && (
          <ScrollView className={cx('list-wrapper')} ref={scrollViewRef} scrollX={false} scrollY onRefresh={onRefresh}>
            <div className={cx({ 'scroll-box': storeOrderInfo?.totalOrders === 0 })}>
              <div className={cx('list-wrapper-content')}>
                <ServiceTimesCard storeOrderInfo={storeOrderInfo} />
                {storeOrderInfo?.totalOrders > 0 ? (
                  <>
                    <StatisticsInfo className={cx('statistics-info-wrapper')} />

                    <div ref={contentRef} className={cx('service-record-title')}>
                      近7天服务详情
                    </div>

                    <WeekTimeSelect currentDate={currentDate} handleDateChange={handleDateChange} />
                    {serviceRecords?.length > 0 ? (
                      <List>
                        <div className={cx('member-list')}>
                          {serviceRecords?.map((item, index) => (
                            <div
                              onClick={() => {
                                drawerRef.current?.show(item.serviceOrderId);
                                sendEvent(TrackerCustomEvent.ServiceRecordDrawerShowTimes, {
                                  et: 'CLK',
                                });
                                addClickRecordCardTimes();
                              }}
                              key={item.createTime}
                            >
                              <ServiceRecordCard record={item} />
                            </div>
                          ))}
                        </div>
                      </List>
                    ) : (
                      <div
                        className={cx('member-empty-wrapper')}
                        style={{ height: scrollHeight * 0.85 - 150.5 + 'px' }}
                      >
                        <EmptyBox />
                      </div>
                    )}
                  </>
                ) : (
                  <EmptyBox className={cx('empty-wrapper-box')} />
                )}
              </div>
            </div>
          </ScrollView>
        )}

        {currentTabKey === 'member-record' && <MemberRecord />}
      </div>
      <ServiceRecordDrawer
        ref={drawerRef}
        addClickGoToSupplyBtnTimes={addClickGoToSupplyBtnTimes}
        refreshList={() => getServiceRecords(currentDate)}
      />
    </div>
  );
};

export default ServiceRecord;
